// Pure payload builder for the bug-report POST. Extracted out of
// `BugReportButton.tsx` to keep that file under the 260-line soft cap and to
// give the metadata composition a single test target.
//
// Dev mobile viewport preview (docs/planning/dev_mobile_viewport_preview.md
// §13a): when the simulator is on, both `state_snapshot.dev_viewport` and
// `dev_sync_metadata.dev_viewport` get populated so triage and the
// sync-and-fix agent can route the note as "recorded against the phone
// simulator" without the reporter having to mention it.
//
// Derived `layout_context` (dev sync follow-up to c238ce65): single-string
// classification of WHAT THE REPORTER WAS LOOKING AT when they filed. Lets
// triage filter "mobile" notes without parsing `browser_info` substrings.
// One of:
//   - "simulated_phone:<preset>:<orientation>" — dev viewport simulator was on
//   - "real_phone"   — viewport width < 768 px (Tailwind `md` threshold)
//   - "real_tablet"  — 768 ≤ width < 1024 px
//   - "desktop"      — width ≥ 1024 px
// The string carries enough info for downstream tooling to also break out
// the preset / orientation without joining other fields.
//
// DevSync §7 branch-aware sync: every payload also carries `git_branch`
// (resolved at build time via vite.config.ts `define:` from
// VERCEL_GIT_COMMIT_REF / `git rev-parse`) and `deploy_host`
// (`window.location.host` at submit time). Both surface at the top of the
// request body AND inside `devSyncMetadata` so the server-side
// `buildDevSyncMetadata` can read either path; the local
// `scripts/dev-sync-export.mjs` then filters notes by branch before
// writing `.dev-sync-queue.json`. See `docs/runbooks/dev_sync_and_fix.md`.

import { APP_GIT_BRANCH, APP_VERSION } from "@/config/version";
import type { AuthContextValue } from "@/contexts/AuthContext";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { readDevViewportSnapshot } from "@/hooks/useDevViewport";
import { readActiveLeadId } from "@/lib/activeLeadIdSurface";
import { readLastInteractionSnapshot } from "@/lib/lastUserInteraction";
import { readOpsLookupFocus } from "@/lib/opsLookup/opsLookupFocusSurface";
import { readOpsObligationFocus } from "@/lib/opsObligations/opsObligationFocusSurface";

function currentDeployHost(): string {
  if (typeof window === "undefined") return "";
  return window.location.host || "";
}

/** Tailwind's `md` and `lg` breakpoints, kept in sync with the app's mobile
 * chrome (`<lg:` = mobile layout). Hard-coded here rather than imported from
 * a config because Tailwind never exposes them as JS constants. */
const MOBILE_BREAKPOINT_PX = 768;
const TABLET_BREAKPOINT_PX = 1024;

export type LayoutContextValue =
  | `simulated_phone:${string}:${string}`
  | "real_phone"
  | "real_tablet"
  | "desktop";

export function deriveLayoutContext(
  devViewport: ReturnType<typeof readDevViewportSnapshot>,
  viewportWidthPx: number,
): LayoutContextValue {
  if (devViewport) return `simulated_phone:${devViewport.preset}:${devViewport.orientation}`;
  if (viewportWidthPx < MOBILE_BREAKPOINT_PX) return "real_phone";
  if (viewportWidthPx < TABLET_BREAKPOINT_PX) return "real_tablet";
  return "desktop";
}

export type BugReportPayloadInput = {
  description: string;
  mode: PrimaryMode;
  repoPath: string;
  auth: AuthContextValue | null;
  scopedOrganizationId: string | null;
  queueForDevSync: boolean;
};

export type BugReportPayloadResult = {
  payload: Record<string, unknown>;
  placement: { repo_path: string } | undefined;
};

function nowRoute(): string {
  if (typeof window === "undefined") return "";
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function nowViewContext(mode: string): string {
  if (typeof document === "undefined") return mode;
  return document.title || mode;
}

export function buildBugReportPayload(input: BugReportPayloadInput): BugReportPayloadResult {
  const { description, mode, repoPath, auth, scopedOrganizationId, queueForDevSync } = input;

  const devViewport = readDevViewportSnapshot();
  const viewportW = typeof window !== "undefined" ? window.innerWidth : 0;
  const viewportH = typeof window !== "undefined" ? window.innerHeight : 0;
  const layoutContext = deriveLayoutContext(devViewport, viewportW);
  const browserBits = [
    typeof navigator !== "undefined" ? navigator.userAgent : "",
    typeof navigator !== "undefined" && navigator.language ? `lang:${navigator.language}` : "",
    typeof navigator !== "undefined" && typeof navigator.onLine === "boolean"
      ? `online:${navigator.onLine}`
      : "",
    typeof window !== "undefined" ? `viewport:${viewportW}x${viewportH}` : "",
    devViewport
      ? `dev_viewport:${devViewport.preset}/${devViewport.orientation}/${devViewport.width}x${devViewport.height}`
      : "",
  ].filter(Boolean);

  const pathTrim = repoPath.trim();
  const placement = pathTrim ? { repo_path: pathTrim } : undefined;

  const routePath = nowRoute();
  const viewContext = nowViewContext(mode);
  const browserInfo = browserBits.join(" | ");
  const gitBranch = APP_GIT_BRANCH || "main";
  const deployHost = currentDeployHost();
  // Active-context capture (see docs/planning/bug_report_active_context.md).
  // Both reads are pure DOM/global-state lookups, safe in SSR (return null).
  const activeLeadId = readActiveLeadId();
  const interactionTarget = readLastInteractionSnapshot();
  const opsLookupFocus = readOpsLookupFocus();
  const opsObligationFocus = readOpsObligationFocus();

  const payload: Record<string, unknown> = {
    description: description.trim(),
    mode,
    version: APP_VERSION,
    git_branch: gitBranch,
    deploy_host: deployHost,
    routePath,
    viewContext,
    browserInfo,
    stateSnapshot: {
      mode,
      activeOrganizationId: scopedOrganizationId,
      activeLocationId: auth?.activeLocationId ?? null,
      dev_viewport: devViewport,
      layout_context: layoutContext,
      active_lead_id: activeLeadId,
      interaction_target: interactionTarget,
      ops_lookup_focus: opsLookupFocus,
      ops_obligation_focus: opsObligationFocus,
    },
    queueForDevSync,
    placement,
    devSyncMetadata: {
      app_version: APP_VERSION,
      git_branch: gitBranch,
      deploy_host: deployHost,
      mode,
      route_path: routePath,
      view_context: viewContext,
      browser_info: browserInfo,
      org_role: auth?.role ?? null,
      platform_role: auth?.platformRole ?? null,
      all_roles: auth?.roles ?? [],
      user_id: auth?.session?.user?.id ?? null,
      reporter_email: auth?.session?.user?.email ?? auth?.profile?.email ?? null,
      organization_id: scopedOrganizationId,
      active_location_id: auth?.activeLocationId ?? null,
      captured_at: new Date().toISOString(),
      dev_viewport: devViewport,
      layout_context: layoutContext,
      active_lead_id: activeLeadId,
      interaction_target: interactionTarget,
      ops_lookup_focus: opsLookupFocus,
      ops_obligation_focus: opsObligationFocus,
    },
  };

  return { payload, placement };
}
